import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Calendar, 
  Clock, 
  ArrowLeft, 
  CheckCircle, 
  ArrowRight, 
  Loader2,
  Info
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { userService } from '@/services/userService';
import { StepLayout } from '../StepLayout';

interface CalendarStepProps {
  onNext: (data: Record<string, any>) => void;
  data?: Record<string, any>;
}

// Componente de passo do stepper interno
const StepperItem = ({ 
  step, 
  isActive, 
  isCompleted, 
  label, 
  description 
}: { 
  step: number; 
  isActive: boolean; 
  isCompleted: boolean; 
  label: React.ReactNode;
  description: string;
}) => {
  return (
    <div className="flex flex-col items-center">
      <div 
        className={cn(
          "flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium transition-colors", 
          isActive && !isCompleted ? "bg-primary text-primary-foreground" : "",
          isCompleted ? "bg-green-100 text-green-700 border-2 border-green-500" : "",
          !isActive && !isCompleted ? "bg-muted text-muted-foreground" : ""
        )}
      >
        {isCompleted ? <CheckCircle className="h-5 w-5" /> : step}
      </div>
      <p className={cn(
        "mt-2 text-sm font-medium", 
        isActive ? "text-primary" : "text-muted-foreground"
      )}>
        {label}
      </p>
      <p className="text-xs text-muted-foreground max-w-[130px] text-center">
        {description}
      </p>
    </div>
  );
};

export const CalendarStep: React.FC<CalendarStepProps> = ({ onNext, data = {} }) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<'auth' | 'config'>(data.credential_id ? 'config' : 'auth');
  const [isCreatingCredential, setIsCreatingCredential] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Configurações do calendário
  const [calendarProvider, setCalendarProvider] = useState(data.calendar_provider || 'google');
  const [availableDays, setAvailableDays] = useState(data.available_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  const [startTime, setStartTime] = useState(data.start_time || '09:00');
  const [endTime, setEndTime] = useState(data.end_time || '18:00');
  const [autoConfirm, setAutoConfirm] = useState(data.auto_confirm ?? true);
  const [bufferTime, setBufferTime] = useState(data.buffer_time || '15');
  const [credentialId, setCredentialId] = useState(data.credential_id || null);
  const [credentialConnected, setCredentialConnected] = useState(data.credential_connected || false);

  // Verificar callback do OAuth na URL
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const oauthCallback = queryParams.get('oauth_callback');
    
    if (oauthCallback === 'true' && data.credential_id) {
      // O usuário retornou após autenticação bem-sucedida
      toast({
        title: "Calendário conectado",
        description: "Sua conta Google Calendar foi conectada com sucesso.",
      });
      
      setCredentialConnected(true);
      setCurrentStep('config');
      
      // Remover parâmetro oauth_callback da URL para evitar problemas ao atualizar a página
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth_callback');
      window.history.replaceState({}, document.title, url.toString());
    }
  }, []);

  const days = [
    { id: 'monday', label: 'Segunda' },
    { id: 'tuesday', label: 'Terça' },
    { id: 'wednesday', label: 'Quarta' },
    { id: 'thursday', label: 'Quinta' },
    { id: 'friday', label: 'Sexta' },
    { id: 'saturday', label: 'Sábado' },
    { id: 'sunday', label: 'Domingo' },
  ];

  const toggleDay = (dayId: string) => {
    if (availableDays.includes(dayId)) {
      setAvailableDays(availableDays.filter(day => day !== dayId));
    } else {
      setAvailableDays([...availableDays, dayId]);
    }
  };

  // Função para salvar o progresso sem avançar para a próxima etapa
  const saveProgress = (calendarData: Record<string, any>) => {
    // Merge com os dados existentes
    const updatedData = {
      ...data,
      ...calendarData
    };
    
    // Usar a mesma função onNext, mas com um flag internal_save para indicar
    // que é apenas para salvar, não para avançar
    onNext({ 
      ...updatedData, 
      __internal_save_only: true
    });
  };

  const handleConnectCalendar = async () => {
    try {
      setIsCreatingCredential(true);
      setError(null);
      
      // Obter o usuário atual
      console.log("Obtendo usuário atual...");
      const user = await userService.getCurrentUser();
      
      if (!user) {
        console.error("Erro: Usuário não autenticado");
        throw new Error("Usuário não autenticado");
      }
      console.log("Usuário autenticado:", user.id);

      // Obter a URL base do Supabase da configuração do projeto
      // Precisamos usar a URL do Supabase para acessar as edge functions
      const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || import.meta.env?.VITE_SUPABASE_URL;
      console.log("SUPABASE_URL:", SUPABASE_URL);
      
      // Obter token de autenticação
      console.log("Obtendo token de autenticação...");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      if (!token) {
        console.error("Erro: Token de autenticação não disponível");
        throw new Error("Token de autenticação não disponível");
      }
      console.log("Token obtido com sucesso (primeiros 10 chars):", token.substring(0, 10) + "...");
      
      // Chamar a edge function para criar a credencial e obter a URL de autorização
      console.log(`Chamando edge function: ${SUPABASE_URL}/functions/v1/n8n-google-calendar-credential`);
      const response = await fetch(`${SUPABASE_URL}/functions/v1/n8n-google-calendar-credential`, {
        method: "POST",
        mode: 'cors',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ userId: user.id }),
      });
      
      console.log("Resposta da edge function - status:", response.status);
      console.log("Resposta da edge function - headers:", Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Erro na resposta da edge function:", errorText);
        try {
          const errorData = JSON.parse(errorText);
          console.error("Detalhes do erro:", errorData);
          throw new Error(errorData.error || "Falha ao criar credencial");
        } catch (parseError) {
          throw new Error(`Falha ao criar credencial: ${errorText}`);
        }
      }
      
      const responseData = await response.json();
      console.log("Resposta da edge function - data:", responseData);
      
      if (!responseData.success || !responseData.authUrl) {
        console.error("Resposta inválida da API:", responseData);
        throw new Error("Resposta inválida da API");
      }
      
      // Salvar o ID da credencial no estado do onboarding
      if (responseData.credential?.id) {
        setCredentialId(responseData.credential.id);
        saveProgress({
          credential_id: responseData.credential.id,
          credential_name: responseData.credential.name,
          calendar_provider: 'google'
        });
      }
      
      // Redirecionar para URL de autorização do Google
      window.location.href = responseData.authUrl;
      
    } catch (err) {
      console.error("Erro ao conectar calendário:", err);
      setError(err.message || "Erro ao conectar calendário. Por favor, tente novamente.");
    } finally {
      setIsCreatingCredential(false);
    }
  };

  const handleContinueToConfig = () => {
    setCurrentStep('config');
  };

  const handleBack = () => {
    setCurrentStep('auth');
  };

  const handleContinue = () => {
    onNext({
      calendar_provider: calendarProvider,
      available_days: availableDays,
      start_time: startTime,
      end_time: endTime,
      auto_confirm: autoConfirm,
      buffer_time: bufferTime,
      credential_id: credentialId,
      credential_connected: credentialConnected,
      configured: true
    });
  };

  // Título e ícone para o StepLayout, com base na etapa atual
  const stepTitle = currentStep === 'auth' 
    ? 'Conectar Calendário' 
    : 'Configurações de Agendamento';
  
  const stepIcon = <Calendar className="h-5 w-5" />;

  return (
    <StepLayout 
      title={stepTitle}
      icon={stepIcon}
    >
      {/* Sub-stepper para as etapas do Calendário */}
      <div className="mb-8">
        <div className="flex justify-center items-start">
          <div className="flex items-center">
            <StepperItem 
              step={1} 
              isActive={false} 
              isCompleted={false} 
              label={
                <div className="flex items-center gap-2">
                  Conectar Calendário
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge 
                          variant="outline" 
                          className="bg-blue-50 text-blue-700 border-blue-200 cursor-help"
                        >
                          Em breve
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Esta funcionalidade estará disponível em breve!</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              }
              description="Autorize o acesso ao Google Calendar"
            />
            <div className="mx-4 w-20 h-0.5 bg-muted relative">
              <div 
                className="absolute top-0 left-0 h-full bg-muted transition-all"
                style={{ width: '0%' }}
              />
            </div>
            <StepperItem 
              step={2} 
              isActive={false} 
              isCompleted={false} 
              label="Configurar Agenda" 
              description="Defina suas preferências de agendamento"
            />
          </div>
        </div>
      </div>
      
      {/* Conteúdo das etapas */}
      <div className="space-y-4">
        <div className="p-6 border rounded-lg bg-muted/10">
          <div className="flex flex-col items-center text-center mb-6">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Integração com Google Calendar</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              A integração com o Google Calendar estará disponível em breve. 
              Estamos trabalhando para trazer esta funcionalidade o mais rápido possível.
            </p>
          </div>
          
          <Button
            disabled
            className="w-full flex items-center justify-center gap-2 cursor-not-allowed"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
              <path d="M6 12.5C6 8.91 8.91 6 12.5 6H17V2.5H12.5C7 2.5 2.5 7 2.5 12.5C2.5 18 7 22.5 12.5 22.5C18 22.5 22.5 18 22.5 12.5V8H17V12.5C17 16.09 14.09 19 10.5 19C6.91 19 4 16.09 4 12.5C4 8.91 6.91 6 10.5 6" fill="currentColor"/>
            </svg>
            Conectar Google Calendar
          </Button>
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                Você pode continuar com a configuração do seu sistema. Assim que a integração com o Google Calendar estiver disponível, você será notificado para completar esta etapa.
              </p>
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  );
}; 